import React, {
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
} from 'react';
import { Plus } from 'lucide-react';
import {
  GridPaginationModel,
  GridSortModel,
  GridFilterModel,
  GridColumnVisibilityModel,
} from '@mui/x-data-grid';
import {
  toDataGridColumns,
  getDefaultColumnVisibility,
} from '../domains/therapeutic-areas/adapters';
import useAuth from 'auth/auth';
import { TherapeuticArea } from '../services/api/domains/therapeutic-areas/types';
import { useTherapeuticAreasPage } from '../services/api/domains/therapeutic-areas/hooks';
import TherapeuticAreaModal, {
  ModalMode,
} from '../components/modals/TherapeuticAreaModal';
import GenericMergeModal from '../components/modals/GenericMergeModal';
import {
  useMergeSuggestions,
  useMergePreview,
  useMergeTherapeuticAreas,
} from '../services/api/domains/therapeutic-areas/hooks';
import TableSearch from 'components/TableSearch';
import {
  SearchMode,
  AdvancedSearchQuery,
  createEmptyAdvancedQuery,
} from 'components/TableSearch/types';
import {
  mergeAdvancedQueries,
  gridFilterToAdvancedQuery,
} from '../utils/dataGridHelpers';
import { therapeuticAreasFieldConfig } from '../config/searchConfigs';
import LgmDataGrid from 'components/LgmDataGrid';

const PAGE_SIZE = 50;

const TherapeuticAreas: React.FC = () => {
  const { isAdmin, userId, isViewer } = useAuth();

  // Search state
  const [searchMode, setSearchMode] = useState<SearchMode>('basic');
  const [basicSearchTerm, setBasicSearchTerm] = useState('');
  const [advancedSearchQuery, setAdvancedSearchQuery] =
    useState<AdvancedSearchQuery>(createEmptyAdvancedQuery());
  const pageRef = useRef(0);

  // DataGrid state
  const [gridFilterModel, setGridFilterModel] = useState<GridFilterModel>({
    items: [],
  });
  const [gridSortModel, setGridSortModel] = useState<GridSortModel>([]);
  const [gridPaginationModel, setGridPaginationModel] =
    useState<GridPaginationModel>({
      page: 0,
      pageSize: PAGE_SIZE,
    });
  const [columnVisibilityModel, setColumnVisibilityModel] =
    useState<GridColumnVisibilityModel>(getDefaultColumnVisibility());

  const therapeuticAreasParams = useMemo(() => {
    // Convert DataGrid filters to AdvancedSearchQuery
    const gridAdvancedQuery = gridFilterToAdvancedQuery(
      gridFilterModel,
      therapeuticAreasFieldConfig
    );

    // Merge with existing search
    let finalAdvancedSearch: AdvancedSearchQuery | undefined;
    if (searchMode === 'advanced') {
      finalAdvancedSearch = mergeAdvancedQueries(
        advancedSearchQuery,
        gridAdvancedQuery
      );
    } else if (gridAdvancedQuery.filterGroups.length > 0) {
      finalAdvancedSearch = gridAdvancedQuery;
    }

    return {
      search:
        searchMode === 'basic'
          ? basicSearchTerm.trim() || undefined
          : undefined,
      advancedSearch: finalAdvancedSearch,
      sortBy: gridSortModel[0]?.field as keyof TherapeuticArea | undefined,
      sortDirection: gridSortModel[0]?.sort?.toUpperCase() as
        | 'ASC'
        | 'DESC'
        | undefined,
      limit: gridPaginationModel.pageSize,
      offset: gridPaginationModel.page * gridPaginationModel.pageSize,
    };
  }, [
    searchMode,
    basicSearchTerm,
    advancedSearchQuery,
    gridFilterModel,
    gridSortModel,
    gridPaginationModel,
  ]);

  const {
    create: createTherapeuticArea,
    update: updateTherapeuticArea,
    delete: deleteTherapeuticArea,
    data: therapeuticAreas,
    total,
    isLoading: isTherapeuticAreasLoading,
    isError: isTherapeuticAreasError,
    error: therapeuticAreasError,
    refetch: refetchTherapeuticAreas,
  } = useTherapeuticAreasPage(therapeuticAreasParams);

  // Preserve total during loading to prevent DataGrid from resetting pagination
  const [stableTotal, setStableTotal] = useState(total || 0);
  useEffect(() => {
    if (total !== undefined && total > 0) {
      setStableTotal(total);
    }
  }, [total]);

  // Modal state for TherapeuticAreaModal
  const [selectedTherapeuticArea, setSelectedTherapeuticArea] =
    useState<TherapeuticArea | null>(null);
  const [openTherapeuticAreaModal, setOpenTherapeuticAreaModal] =
    useState<ModalMode | null>(null);

  // Modal state for Merge
  const [mergeSourceTherapeuticArea, setMergeSourceTherapeuticArea] =
    useState<TherapeuticArea | null>(null);
  const [openMergeModal, setOpenMergeModal] = useState(false);

  // DataGrid columns from adapter
  const therapeuticAreasColumns = useMemo(() => toDataGridColumns(), []);

  // Reset page when search changes
  useEffect(() => {
    if (pageRef.current !== 0) {
      console.log('Resetting page to 0 due to search change');
      setGridPaginationModel(prev => ({ ...prev, page: 0 }));
    }
  }, [basicSearchTerm, advancedSearchQuery]);

  useEffect(() => {
    pageRef.current = gridPaginationModel.page;
  }, [gridPaginationModel.page]);

  // Save handler for the new modal system
  const handleSaveTherapeuticArea = useCallback(
    async (therapeuticAreaData: TherapeuticArea) => {
      try {
        if (selectedTherapeuticArea?.therapeutic_area_id) {
          // Edit existing therapeutic area - include user_id
          await updateTherapeuticArea.mutate({
            ...therapeuticAreaData,
            user_id: userId,
          });
        } else {
          // Add new therapeutic area - include user_id
          await createTherapeuticArea.mutate({
            therapeutic_area_name: therapeuticAreaData.therapeutic_area_name,
            user_id: userId,
          });
        }
        setOpenTherapeuticAreaModal(null);
        setSelectedTherapeuticArea(null);
        refetchTherapeuticAreas();
        alert('✅ Therapeutic area saved successfully');
      } catch (err) {
        console.error('❌ Save therapeutic area failed:', err);
        alert('❌ Failed to save therapeutic area. Please try again.');
      }
    },
    [
      selectedTherapeuticArea,
      updateTherapeuticArea,
      createTherapeuticArea,
      refetchTherapeuticAreas,
      userId,
    ]
  );

  const handleCloseTherapeuticAreaModal = useCallback(() => {
    setOpenTherapeuticAreaModal(null);
    setSelectedTherapeuticArea(null);
  }, []);

  // Memoized handlers for modal actions
  const handleAddTherapeuticArea = useCallback(() => {
    const newTherapeuticArea: Partial<TherapeuticArea> = {
      therapeutic_area_name: '',
      created_date: new Date(),
      modified_date: new Date(),
      user_id: userId,
    };
    setSelectedTherapeuticArea(newTherapeuticArea as TherapeuticArea);
    setOpenTherapeuticAreaModal('EDIT');
  }, [userId]);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Delete this therapeutic area?')) return;
    try {
      await deleteTherapeuticArea.mutate(id);
      refetchTherapeuticAreas();
      alert('✅ Therapeutic area deleted successfully');
    } catch (err) {
      console.error('❌ Delete failed:', err);
      alert('❌ Failed to delete therapeutic area. Please try again.');
    }
  };

  const handleMerge = useCallback((therapeuticArea: TherapeuticArea) => {
    setMergeSourceTherapeuticArea(therapeuticArea);
    setOpenMergeModal(true);
  }, []);

  const handleCloseMergeModal = useCallback(() => {
    setOpenMergeModal(false);
    setMergeSourceTherapeuticArea(null);
  }, []);

  const handleMergeSuccess = useCallback(() => {
    refetchTherapeuticAreas();
    alert('✅ Therapeutic areas merged successfully');
  }, [refetchTherapeuticAreas]);

  return (
    <div style={styles.pageContainerStyle}>
      <div style={styles.searchBarContainerStyle}>
        <div style={styles.searchUploadContainerStyle}>
          <TableSearch
            mode={searchMode}
            onModeChange={setSearchMode}
            basicSearchTerm={basicSearchTerm}
            onBasicSearchChange={setBasicSearchTerm}
            onBasicSearchClear={() => setBasicSearchTerm('')}
            basicPlaceholder="Search therapeutic areas..."
            advancedFieldConfigs={therapeuticAreasFieldConfig}
            advancedQuery={advancedSearchQuery}
            onAdvancedSearch={query => {
              setAdvancedSearchQuery(query);
              setGridPaginationModel(prev => ({ ...prev, page: 0 }));
            }}
            onAdvancedClear={() => {
              setAdvancedSearchQuery(createEmptyAdvancedQuery());
              setGridPaginationModel(prev => ({ ...prev, page: 0 }));
            }}
          />
          {isAdmin && (
            <button
              onClick={handleAddTherapeuticArea}
              style={styles.addButtonStyle}>
              <Plus size={16} /> Add Therapeutic Area
            </button>
          )}
        </div>

        {/* Loading State */}
        {isTherapeuticAreasLoading && (
          <div style={styles.loadingStyle}>
            <span style={styles.loadingTextStyle}>
              Loading therapeutic areas...
            </span>
          </div>
        )}

        {/* Error State */}
        {isTherapeuticAreasError && (
          <div style={styles.errorStyle}>
            Error loading therapeutic areas: {therapeuticAreasError?.message}
          </div>
        )}
      </div>

      <div style={styles.tableContainerStyle}>
        <LgmDataGrid<TherapeuticArea>
          data={therapeuticAreas}
          columns={therapeuticAreasColumns}
          loading={isTherapeuticAreasLoading}
          rowCount={stableTotal}
          paginationModel={gridPaginationModel}
          onPaginationModelChange={setGridPaginationModel}
          sortModel={gridSortModel}
          onSortModelChange={setGridSortModel}
          filterModel={gridFilterModel}
          onFilterModelChange={setGridFilterModel}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          getRowId={row => row.therapeutic_area_id}
          onInfo={(t: TherapeuticArea) => {
            setSelectedTherapeuticArea(t);
            setOpenTherapeuticAreaModal('VIEW');
          }}
          onEdit={
            !isViewer
              ? (t: TherapeuticArea) => {
                  setSelectedTherapeuticArea(t);
                  setOpenTherapeuticAreaModal('EDIT');
                }
              : undefined
          }
          onDelete={
            !isViewer
              ? (t: TherapeuticArea) => {
                  handleDelete(t.therapeutic_area_id);
                }
              : undefined
          }
          onMerge={isAdmin ? (t: TherapeuticArea) => handleMerge(t) : undefined}
        />
      </div>

      {/* TherapeuticAreaModal */}
      {openTherapeuticAreaModal && selectedTherapeuticArea && (
        <TherapeuticAreaModal
          therapeuticArea={selectedTherapeuticArea}
          initialMode={openTherapeuticAreaModal}
          save={handleSaveTherapeuticArea}
          close={handleCloseTherapeuticAreaModal}
        />
      )}

      {/* GenericMergeModal for Therapeutic Areas */}
      {openMergeModal && mergeSourceTherapeuticArea && (
        <GenericMergeModal<TherapeuticArea>
          source={mergeSourceTherapeuticArea}
          allEntities={therapeuticAreas}
          config={{
            domainDisplayName: 'Therapeutic Area',
            domainDisplayNamePlural: 'Therapeutic Areas',
            entityIdField: 'therapeutic_area_id',
            entityNameField: 'therapeutic_area_name',
            entityCreatedDateField: 'created_date',
            useMergeSuggestions,
            useMergePreview,
            useMerge: useMergeTherapeuticAreas,
          }}
          onClose={handleCloseMergeModal}
          onSuccess={handleMergeSuccess}
        />
      )}
    </div>
  );
};

const styles: Record<string, React.CSSProperties> = {
  searchUploadContainerStyle: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
  } as React.CSSProperties,
  titleStyle: {
    fontSize: '1.25rem', // text-xl
    fontWeight: 'bold',
  } as React.CSSProperties,
  addButtonStyle: {
    backgroundColor: '#2563eb', // bg-blue-600
    color: 'white',
    padding: '0.5rem 1rem', // px-4 py-2
    borderRadius: '0.25rem', // rounded
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem', // gap-2
    border: 'none',
    cursor: 'pointer',
  } as React.CSSProperties,
  loadingStyle: {
    textAlign: 'center',
    padding: '1rem 0', // py-4
  } as React.CSSProperties,
  loadingTextStyle: {
    color: '#4b5563', // text-gray-600
  } as React.CSSProperties,
  errorStyle: {
    backgroundColor: '#fecaca', // bg-red-100
    border: '1px solid #f87171', // border-red-400
    color: '#b91c1c', // text-red-700
    padding: '0.75rem 1rem', // px-4 py-3
    borderRadius: '0.25rem', // rounded
    marginBottom: '1rem', // mb-4
  } as React.CSSProperties,
  pageContainerStyle: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  } as React.CSSProperties,
  searchBarContainerStyle: {
    flexShrink: 0,
    backgroundColor: '#ffffff',
    borderBottom: '1px solid #d1d5db',
    paddingBottom: '8px',
  } as React.CSSProperties,
  tableContainerStyle: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    minHeight: 0,
  } as React.CSSProperties,
};

export default TherapeuticAreas;
